import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { doctorAdminApi } from "../../services/doctorAdmin.api";
import {
  AdminDoctorDetailResponse,
  AdminDoctorLicence,
  DoctorAdminActionAuditEntry,
  DoctorLicenceGrantHistoryEntry,
  DoctorPublicResponse,
  SmsStatusResponse,
} from "../../types/doctorAdmin.definition";
import { SearchResponse } from "../../types/search.definition";
import {
  fetchAuditLogFailure,
  fetchAuditLogRequest,
  fetchAuditLogSuccess,
  fetchDoctorDetailFailure,
  fetchDoctorDetailRequest,
  fetchDoctorDetailSuccess,
  fetchDoctorsFailure,
  fetchDoctorsRequest,
  fetchDoctorsSuccess,
  fetchGrantHistoryFailure,
  fetchGrantHistoryRequest,
  fetchGrantHistorySuccess,
  fetchSmsStatusFailure,
  fetchSmsStatusRequest,
  fetchSmsStatusSuccess,
  grantFreeAccessFailure,
  grantFreeAccessRequest,
  grantFreeAccessSuccess,
} from "../reducers/doctorAdminSlice";

// Helper to extract an error code from rejected interceptor errors.
function extractErrorCode(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as Record<string, unknown>).code === "string"
  ) {
    return (error as Record<string, unknown>).code as string;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "UNKNOWN_ERROR";
}

function* fetchDoctorsSaga(action: ReturnType<typeof fetchDoctorsRequest>) {
  try {
    const data: SearchResponse<DoctorPublicResponse> = yield call(
      doctorAdminApi.searchDoctors,
      action.payload
    );
    yield put(fetchDoctorsSuccess(data));
  } catch (error) {
    yield put(fetchDoctorsFailure(extractErrorCode(error)));
  }
}

function* fetchDoctorDetailSaga(
  action: ReturnType<typeof fetchDoctorDetailRequest>
) {
  try {
    const data: AdminDoctorDetailResponse = yield call(
      doctorAdminApi.getDoctorDetail,
      action.payload
    );
    yield put(fetchDoctorDetailSuccess(data));
  } catch (error) {
    yield put(fetchDoctorDetailFailure(extractErrorCode(error)));
  }
}

function* grantFreeAccessSaga(
  action: ReturnType<typeof grantFreeAccessRequest>
) {
  const { doctorId, data, onSuccess, onError } = action.payload;
  try {
    const licence: AdminDoctorLicence = yield call(
      doctorAdminApi.grantFreeAccess,
      doctorId,
      data
    );
    yield put(grantFreeAccessSuccess({ doctorId, licence }));
    if (onSuccess) onSuccess(licence);
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(grantFreeAccessFailure(code));
    if (onError) onError(code);
  }
}

function* fetchGrantHistorySaga(
  action: ReturnType<typeof fetchGrantHistoryRequest>
) {
  try {
    const data: DoctorLicenceGrantHistoryEntry[] = yield call(
      doctorAdminApi.getGrantHistory,
      action.payload
    );
    yield put(fetchGrantHistorySuccess(data));
  } catch (error) {
    yield put(fetchGrantHistoryFailure(extractErrorCode(error)));
  }
}

function* fetchSmsStatusSaga(action: ReturnType<typeof fetchSmsStatusRequest>) {
  try {
    const data: SmsStatusResponse = yield call(
      doctorAdminApi.getDoctorSmsStatus,
      action.payload
    );
    yield put(fetchSmsStatusSuccess(data));
  } catch (error) {
    yield put(fetchSmsStatusFailure(extractErrorCode(error)));
  }
}

function* fetchAuditLogSaga(action: ReturnType<typeof fetchAuditLogRequest>) {
  try {
    const data: DoctorAdminActionAuditEntry[] = yield call(
      doctorAdminApi.getAdminActionAudit,
      action.payload
    );
    yield put(fetchAuditLogSuccess(data));
  } catch (error) {
    yield put(fetchAuditLogFailure(extractErrorCode(error)));
  }
}

export function* doctorAdminSaga() {
  yield takeLatest(fetchDoctorsRequest, fetchDoctorsSaga);
  yield takeLatest(fetchDoctorDetailRequest, fetchDoctorDetailSaga);
  yield takeEvery(grantFreeAccessRequest, grantFreeAccessSaga);
  yield takeLatest(fetchGrantHistoryRequest, fetchGrantHistorySaga);
  yield takeLatest(fetchSmsStatusRequest, fetchSmsStatusSaga);
  yield takeLatest(fetchAuditLogRequest, fetchAuditLogSaga);
}
